import React from 'react';
import { ArrowRight } from 'lucide-react';
// Labels and links come from the shared category tree (data/categoryNav.ts).
import { getNavGroup, getNavItem, NavGroupId } from '../../data/categoryNav';
const sub = (groupId: NavGroupId, label: string) => {
  const item = getNavItem(groupId, label);
  return {
    name: item.label,
    href: item.href
  };
};
const PRIORITY_CARDS = [{
  ...sub('mailboxes', 'Complete Mailbox & Post Systems'),
  desc: 'Everything you need in one matching package.',
  image: 'https://cdn.magicpatterns.com/uploads/tQSFov8NC91yLMr3M1DE87/06-popular-complete-mailbox-system.png',
  alt: 'Complete black mailbox and post system.',
  stage: '#F7F3EB'
}, {
  name: getNavGroup('commercial').fullLabel,
  href: getNavGroup('commercial').href,
  desc: 'Commercial delivery systems with project-specific requirements.',
  image: 'https://cdn.magicpatterns.com/uploads/jz81zFe422w3g9Af4dbGAz/midatlantic-florence-1570-cbu-warm-stage.png',
  alt: 'Florence 1570 cluster box mailbox unit for centralized delivery.'
}, {
  ...sub('mailboxes', 'Mailbox Parts & Accessories'),
  desc: 'Doors, locks, keys, and hardware.',
  image: 'https://asset.imagine.art/processed/f08a3a69-b54f-49fa-9524-ddb09a8d0093',
  alt: 'Replacement mailbox locks, keys, doors, and hardware.',
  imageFit: 'cover'
}];
const SUPPORTING_CARDS = [{
  ...sub('mailboxes', 'Wall Mount Mailboxes'),
  icon: 'https://cdn.magicpatterns.com/uploads/bpWPSBcBJtjAbd2BkNYFo/06-wall-mount-mailboxes-icon.png'
}, {
  ...sub('mailboxes', 'Locking Mailboxes'),
  icon: 'https://cdn.magicpatterns.com/uploads/7gU6R6Um6Mpn2A5oVV66fx/07-locking-mailboxes-icon.png'
}, {
  ...sub('mailboxes', 'Mailbox Posts'),
  icon: 'https://cdn.magicpatterns.com/uploads/nR8YdkgpBC3RKdTvQjPmvj/02-match-post-finish-size-mounting-icon.png'
}, {
  ...sub('outdoor-accessories', 'Address Plaques & House Numbers'),
  icon: 'https://cdn.magicpatterns.com/uploads/dyStmSj8ojw7PZza7qUEUX/03-house-numbers-personalization-icon.png'
}, {
  name: getNavGroup('street-signs').fullLabel,
  href: getNavGroup('street-signs').href,
  icon: 'https://cdn.magicpatterns.com/uploads/e8T4isAFkWvNvYfBLmMEz4/08-street-signs-traffic-signs-icon.png'
}, {
  name: getNavGroup('lighting').fullLabel,
  href: getNavGroup('lighting').href,
  icon: 'https://cdn.magicpatterns.com/uploads/m1tx7bnvEKFg6bMPozXrwK/09-lighting-icon.png'
}, {
  name: getNavGroup('outdoor-accessories').fullLabel,
  href: getNavGroup('outdoor-accessories').href,
  icon: 'https://cdn.magicpatterns.com/uploads/qooystnYKciwAXKfzn2z2J/10-home-garden-accents-icon.png'
}];
export function PopularPaths() {
  return <section className="py-16 lg:py-24 bg-white border-y border-brand-border" data-id="element-427" data-mp="3sxnyjke4682u">
      <div className="max-w-7xl mx-auto px-4" data-id="element-428" data-mp="2vfxf13dn7l2r">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4" data-id="element-429" data-mp="30f0iwkhvzl95">
          <div className="max-w-2xl" data-id="element-430" data-mp="2sviwmzd9ch8w">
            <h2 className="text-3xl font-serif font-bold text-brand-charcoal mb-3" data-id="element-431" data-mp="2suga13myx0ys">
              Popular shopping paths
            </h2>
            <p className="text-brand-muted text-lg" data-id="element-432" data-mp="2nuv9ios5gkjy">
              Organized around real buying decisions, not just a long category
              tree.
            </p>
          </div>
          <a href="/29.html" className="hidden md:flex items-center text-brand-charcoal font-semibold hover:text-brand-maroon transition-colors" data-id="element-433" data-mp="2evqdbp4293ay">
            View all categories{' '}
            <ArrowRight size={16} className="ml-2 text-brand-maroon" data-id="element-434" data-mp="254dhynj1zv06" />
          </a>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6" data-id="element-435" data-mp="204r3karho8ps">
          {PRIORITY_CARDS.map(cat => <a key={cat.name} href={cat.href} className="group flex flex-col bg-white rounded-xl border border-brand-border overflow-hidden hover:border-brand-maroon/60 hover:shadow-sm transition-all" data-id="element-436" data-mp="1r65r8xcm1pxg">
              <div className="product-image-stage h-48 flex items-center justify-center border-b border-brand-border/50 overflow-hidden" style={cat.stage ? {
            backgroundColor: cat.stage
          } : undefined} data-id="element-437" data-mp="3ma3pg4pox7vj">
                <img src={cat.image} alt={cat.alt} className={`h-full w-full ${cat.imageFit === 'cover' ? 'object-cover' : 'object-contain'} object-center transform group-hover:scale-105 transition-transform duration-300`} data-id="element-438" data-mp="32kjkj5cq4jss" />
              </div>
              <div className="p-6 bg-white" data-id="element-439" data-mp="2yksoam4fhbgv">
                <h3 className="font-serif font-bold text-lg text-brand-charcoal mb-1 group-hover:text-brand-maroon transition-colors" data-id="element-440" data-mp="rtjpwrre2uji">
                  {cat.name}
                </h3>
                <p className="text-sm text-brand-muted" data-id="element-441" data-mp="wqa6dg73vs5g">{cat.desc}</p>
              </div>
            </a>)}
        </div>

        <div className="flex flex-wrap justify-center gap-4" data-id="element-442" data-mp="11y06jncp0fod">
          {SUPPORTING_CARDS.map(cat => <a key={cat.name} href={cat.href} className="group bg-white rounded-lg border border-brand-border p-4 text-center hover:border-brand-maroon/60 hover:bg-brand-goldWash transition-all flex flex-col items-center justify-center gap-2 min-h-[110px] w-[calc(50%-0.5rem)] md:w-[calc(33.333%-0.75rem)] lg:w-[calc(25%-0.75rem)]" data-id="element-443" data-mp="1fp4y025wq1nm">
              <img src={cat.icon} alt="" aria-hidden="true" className="h-12 sm:h-14 lg:h-[72px] w-auto object-contain transition-transform duration-300 group-hover:scale-105" data-id="element-444" data-mp="1yo9535dw1lcn" />
              <h3 className="font-medium text-brand-charcoal text-sm group-hover:text-brand-maroon transition-colors leading-snug" data-id="element-445" data-mp="49zsyriazr0u">
                {cat.name}
              </h3>
            </a>)}
        </div>

        <div className="mt-8 text-center md:hidden" data-id="element-446" data-mp="1zf8yu9ry90vb">
          <a href="/29.html" className="inline-flex items-center justify-center w-full bg-white text-brand-charcoal font-semibold py-3 rounded-md border border-brand-border hover:border-brand-maroon hover:bg-brand-goldWash transition-colors" data-id="element-447" data-mp="24cqxn7c95zpl">
            View all categories
          </a>
        </div>
      </div>
    </section>;
}